import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import { cn } from "../../../cn.ts";

/**
 * § Primitives, Badge, taken from shadcn/ui's Badge, with Spectrum UI's Status Badge
 * (`@spectrumui/status-badge`, Apache-2.0) for the medium size, and themed through the
 * variables of `packages/tokens` (2026-09-15, by the owner's decision): static only, a fully
 * round pill. Chip is the same pill with its interaction.
 *
 * What differs from the source, and why: the colour comes from the Team accent (`primary`, the one a
 * Team's palette recolours, for a badge that marks what is current, chosen or the default), Baton's
 * eight fixed accents and the neutral set in the three variants the design system names (soft, solid,
 * outline) rather
 * than shadcn/ui's six role variants and Status Badge's Tailwind palette, because a badge's
 * colour carries a meaning (§ Meaning colours); an amber or orange solid carries the strong
 * border its boundary needs to reach 3:1 (§ Accent ramps); the neutral soft badge sets the
 * secondary text on the muted fill, 4.51:1, where the earlier neutral fill held 4.23:1; the
 * small size is the 20 px pill of § Component sizes and the medium the Status Badge's 28 px
 * pill with its 13 px label, both where the source writes arbitrary lengths.
 *
 * 2026-10-03, by the owner's approval of the product review, which found filled pills shouting on
 * every screen: a badge is `quiet` unless its host asks for a fill or an edge, the same calm mark
 * `StatusPill` draws: no fill and no edge, a dot in the accent's solid (or the host's icon in the
 * accent's text colour) before a sentence-case label in the secondary ink. Colour stays in the words
 * only where it carries a meaning the dot alone would not: red, which says something failed or is
 * late, and the Team accent's `primary`, which marks the current or default thing.
 */
export type Accent = "primary" | "blue" | "green" | "amber" | "red" | "purple" | "teal" | "pink" | "orange" | "neutral";
export type ChipVariant = "soft" | "solid" | "outline";
export type BadgeVariant = ChipVariant | "quiet";

/** The dot of a quiet badge, in the accent's solid, or the Team accent's line for `primary`. */
const QUIET_DOT: Readonly<Record<Accent, string>> = {
  primary: "bg-accent-line",
  blue: "bg-accent-blue-solid",
  green: "bg-accent-green-solid",
  amber: "bg-accent-amber-solid",
  red: "bg-accent-red-solid",
  purple: "bg-accent-purple-solid",
  teal: "bg-accent-teal-solid",
  pink: "bg-accent-pink-solid",
  orange: "bg-accent-orange-solid",
  neutral: "bg-text-secondary",
};

/** The icon of a quiet badge, in the accent's text colour. */
const QUIET_ICON: Readonly<Record<Accent, string>> = {
  primary: "text-accent-text",
  blue: "text-accent-blue-text",
  green: "text-accent-green-text",
  amber: "text-accent-amber-text",
  red: "text-accent-red-text",
  purple: "text-accent-purple-text",
  teal: "text-accent-teal-text",
  pink: "text-accent-pink-text",
  orange: "text-accent-orange-text",
  neutral: "text-text-tertiary",
};

/** The two light solids whose boundary needs the strong outline to reach 3:1. */
export const OUTLINED_ACCENTS: ReadonlySet<Accent> = new Set(["amber", "orange"]);

export const badgeVariants = cva("inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent whitespace-nowrap transition-[color,box-shadow] [&>svg]:pointer-events-none [&>svg]:size-3", {
  variants: {
    accent: { primary: "", blue: "", green: "", amber: "", red: "", purple: "", teal: "", pink: "", orange: "", neutral: "" },
    variant: { soft: "", solid: "", outline: "", quiet: "" },
    size: {
      sm: "h-(--chip-height) px-(--chip-padding-x) text-xs font-semibold",
      md: "h-(--chip-height-md) gap-1.5 px-3 text-small leading-none font-medium",
    },
  },
  compoundVariants: [
    { variant: "soft", accent: "primary", class: "bg-accent-bg text-accent-text" },
    { variant: "soft", accent: "blue", class: "bg-accent-blue-bg text-accent-blue-text" },
    { variant: "soft", accent: "green", class: "bg-accent-green-bg text-accent-green-text" },
    { variant: "soft", accent: "amber", class: "bg-accent-amber-bg text-accent-amber-text" },
    { variant: "soft", accent: "red", class: "bg-accent-red-bg text-accent-red-text" },
    { variant: "soft", accent: "purple", class: "bg-accent-purple-bg text-accent-purple-text" },
    { variant: "soft", accent: "teal", class: "bg-accent-teal-bg text-accent-teal-text" },
    { variant: "soft", accent: "pink", class: "bg-accent-pink-bg text-accent-pink-text" },
    { variant: "soft", accent: "orange", class: "bg-accent-orange-bg text-accent-orange-text" },
    { variant: "soft", accent: "neutral", class: "bg-muted text-muted-foreground" },
    { variant: "solid", accent: "primary", class: "accent-edge bg-accent-solid text-accent-on-solid" },
    { variant: "solid", accent: "blue", class: "bg-accent-blue-solid text-accent-blue-on-solid" },
    { variant: "solid", accent: "green", class: "bg-accent-green-solid text-accent-green-on-solid" },
    { variant: "solid", accent: "amber", class: "border-border-strong bg-accent-amber-solid text-accent-amber-on-solid" },
    { variant: "solid", accent: "red", class: "bg-accent-red-solid text-accent-red-on-solid" },
    { variant: "solid", accent: "purple", class: "bg-accent-purple-solid text-accent-purple-on-solid" },
    { variant: "solid", accent: "teal", class: "bg-accent-teal-solid text-accent-teal-on-solid" },
    { variant: "solid", accent: "pink", class: "bg-accent-pink-solid text-accent-pink-on-solid" },
    { variant: "solid", accent: "orange", class: "border-border-strong bg-accent-orange-solid text-accent-orange-on-solid" },
    { variant: "solid", accent: "neutral", class: "bg-muted-foreground text-background" },
    { variant: "outline", accent: "primary", class: "border-accent-line text-accent-text" },
    { variant: "outline", accent: "blue", class: "border-accent-blue-solid text-accent-blue-text" },
    { variant: "outline", accent: "green", class: "border-accent-green-solid text-accent-green-text" },
    { variant: "outline", accent: "amber", class: "border-accent-amber-solid text-accent-amber-text" },
    { variant: "outline", accent: "red", class: "border-accent-red-solid text-accent-red-text" },
    { variant: "outline", accent: "purple", class: "border-accent-purple-solid text-accent-purple-text" },
    { variant: "outline", accent: "teal", class: "border-accent-teal-solid text-accent-teal-text" },
    { variant: "outline", accent: "pink", class: "border-accent-pink-solid text-accent-pink-text" },
    { variant: "outline", accent: "orange", class: "border-accent-orange-solid text-accent-orange-text" },
    { variant: "outline", accent: "neutral", class: "border-input text-muted-foreground" },
    { variant: "quiet", class: "rounded-none px-0 font-medium text-text-tertiary" },
    { variant: "quiet", accent: "primary", class: "text-accent-text" },
    { variant: "quiet", accent: "red", class: "text-accent-red-text" },
  ],
  defaultVariants: {
    accent: "neutral",
    variant: "soft",
    size: "sm",
  },
});

export interface BadgeProps {
  readonly accent?: Accent;
  readonly variant?: BadgeVariant;
  readonly size?: "sm" | "md";
  readonly icon?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}

export function Badge({ accent = "neutral", variant = "quiet", size = "sm", icon, children, className }: BadgeProps) {
  const quiet = variant === "quiet";
  return (
    <span data-slot="badge" data-accent={accent} data-variant={variant} data-outlined={variant === "solid" && OUTLINED_ACCENTS.has(accent) ? "true" : undefined} className={cn(badgeVariants({ accent, variant, size }), className)}>
      {icon !== undefined ? (
        <span className={cn("inline-flex [&>svg]:size-3", quiet && QUIET_ICON[accent])} aria-hidden="true">
          {icon}
        </span>
      ) : (
        quiet && <span data-slot="badge-dot" className={cn("size-2 flex-none rounded-full", QUIET_DOT[accent])} aria-hidden="true" />
      )}
      {children}
    </span>
  );
}
